import React, { useEffect } from 'react';
import { FormikSlider } from './FormikSlider';
import { useField, useFormikContext } from 'formik';
import type { FormField } from '../interfaces';
import { findCamundaPropertyValue, getLocalizedLabel } from '../utils';
import { useGetVurderingsskjemaQuery } from '@klp/prosjekt/services/rtkApi';
import { Loader } from '@klp/design/Loader';
import { FormikNumericFormat } from './FormikNumericFormat';

export const VurderingsskjemaSelector = ({
    isRequired,
    isReadOnly,
    field,
    defaultPercentage,
    percentageMarks,
    prosessInstansId,
}: {
    field: FormField;
    isReadOnly?: boolean;
    isRequired?: boolean;
    defaultPercentage: number;
    percentageMarks?: Array<{ value: number; label: string }>;
    prosessInstansId?: string;
}) => {
    const [formikField, meta] = useField(field?.id as string);

    const subtype = findCamundaPropertyValue('subtype', field?.properties);

    const { setFieldValue } = useFormikContext();

    const { data, isLoading, isFetching } = useGetVurderingsskjemaQuery(
        { prosessInstansId },
        { skip: !prosessInstansId },
    );

    useEffect(() => {
        if (field?.id) {
            if (subtype === 'percentslider') {
                const value =
                    data?.vurderingsoppsummering?.andelKostnadsforing ??
                    formikField?.value;
                setFieldValue(field?.id, value.toString());
            } else {
                const value =
                    data?.vurderingsoppsummering?.sumTotal ??
                    formikField?.value;
                setFieldValue(field?.id, value.toString());
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetching, isLoading, data, field, formikField?.value, subtype]);

    return (
        <Loader isLoading={isLoading || isFetching}>
            {subtype === 'percentslider' ? (
                <FormikSlider
                    field={field}
                    defaultPercentage={defaultPercentage}
                    percentageMarks={percentageMarks}
                    isReadOnly={isReadOnly}
                    isRequired={isRequired}
                    label={getLocalizedLabel(
                        `project.formFields.${field?.id}`,
                        field?.label,
                        field?.id,
                    )}
                />
            ) : (
                <FormikNumericFormat
                    variant="outlined"
                    id={field?.id}
                    placeholder={
                        findCamundaPropertyValue(
                            'placeholder',
                            field?.properties,
                        ) || ''
                    }
                    defaultValue={field?.value ?? field?.defaultValue}
                    isRequired={isRequired}
                    isReadOnly={isReadOnly}
                    label={getLocalizedLabel(
                        `project.formFields.${field?.id}`,
                        field?.label,
                        field?.id,
                    )}
                    helperText={meta.touched ? meta.error : ''}
                    error={meta.touched && Boolean(meta.error)}
                />
            )}
        </Loader>
    );
};
